import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  FiActivity,
  FiBarChart2,
  FiBook,
  FiClipboard,
  FiMail,
  FiMessageCircle,
  FiKey,
  FiSend,
  FiTool,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type { UserResponse } from '@/lib/types';

const ADMIN_PHONE = '+17148667275';
const ADMIN_DASHBOARD_HREF = import.meta.env.DEV
  ? 'http://localhost:5173/admin/'
  : 'https://harborcoparent.com/admin/';

function AdminLayout() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const userQuery = useQuery({
    queryKey: ['admin', 'user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
  });

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/auth/sign-in' });
    }
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!userQuery.isFetched || !userQuery.data) return;
    if ((userQuery.data.phone_number ?? '').trim() !== ADMIN_PHONE) {
      navigate({ to: '/', search: { hero: undefined } });
    }
  }, [userQuery.isFetched, userQuery.data, navigate]);

  if (!isLoaded || userQuery.isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (!isSignedIn || !userQuery.data) return null;
  if ((userQuery.data.phone_number ?? '').trim() !== ADMIN_PHONE) return null;

  const usersActive =
    pathname === '/admin' ||
    pathname === '/admin/' ||
    pathname.startsWith('/admin/users');
  const pulseActive = pathname.startsWith('/admin/pulse');
  const prospectsActive = pathname.startsWith('/admin/prospects');
  const coachOutreachActive = pathname.startsWith('/admin/coach-outreach');
  const directoryProfilesActive = pathname.startsWith('/admin/directory-profiles');
  const toolsActive = pathname.startsWith('/admin/tools');
  const professionalCodesActive = pathname.startsWith('/admin/professional-codes');
  const journalActive = pathname.startsWith('/admin/journal');
  const chatsActive = pathname.startsWith('/admin/chats');
  const dripActive = pathname.startsWith('/admin/drip-campaigns');
  const adsActive = pathname.startsWith('/admin/ads');
  const tabBase =
    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
  const tabInactive = `${tabBase} text-gray-500 hover:text-gray-700`;
  const tabActive = `${tabBase} border border-gray-200 bg-harbor-50 text-primary`;

  return (
    <div className='min-h-screen bg-gray-50'>
      <header className='sticky top-0 z-40 bg-white border-b border-gray-200 px-6 py-4'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <a
            href={ADMIN_DASHBOARD_HREF}
            className='flex items-center gap-3 text-xl font-semibold text-gray-900 hover:text-gray-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg'
          >
            <img
              src='/favicon.png'
              alt=''
              width={36}
              height={36}
              className='h-9 w-9 shrink-0 rounded-lg border border-sky-200 bg-white object-cover'
            />
            Harbor Admin
          </a>
          <span className='text-sm text-gray-500'>
            {userQuery.data.first_name} {userQuery.data.last_name}
          </span>
        </div>
        <nav className='mt-5 flex flex-wrap items-center gap-2'>
          <Link to='/admin' className={usersActive ? tabActive : tabInactive}>
            <FiUsers className='h-4 w-4 shrink-0' aria-hidden />
            Users
          </Link>
          <Link
            to='/admin/pulse'
            search={{ date: undefined }}
            className={pulseActive ? tabActive : tabInactive}
          >
            <FiActivity className='h-4 w-4 shrink-0' aria-hidden />
            Pulse
          </Link>
          <Link
            to='/admin/prospects'
            className={prospectsActive ? tabActive : tabInactive}
          >
            <FiUserPlus className='h-4 w-4 shrink-0' aria-hidden />
            Prospects
          </Link>
          <Link
            to='/admin/drip-campaigns'
            search={{ range: '30d', campaign: undefined }}
            className={dripActive ? tabActive : tabInactive}
          >
            <FiMail className='h-4 w-4 shrink-0' aria-hidden />
            Drip Campaigns
          </Link>
          <Link
            to='/admin/ads'
            search={{ days: '30' }}
            className={adsActive ? tabActive : tabInactive}
          >
            <FiBarChart2 className='h-4 w-4 shrink-0' aria-hidden />
            Ads
          </Link>
          <Link
            to='/admin/coach-outreach'
            className={coachOutreachActive ? tabActive : tabInactive}
          >
            <FiSend className='h-4 w-4 shrink-0' aria-hidden />
            Coach Outreach
          </Link>
          <Link
            to='/admin/directory-profiles'
            className={directoryProfilesActive ? tabActive : tabInactive}
          >
            <FiClipboard className='h-4 w-4 shrink-0' aria-hidden />
            Directory Profiles
          </Link>
          <Link
            to='/admin/professional-codes'
            className={professionalCodesActive ? tabActive : tabInactive}
          >
            <FiKey className='h-4 w-4 shrink-0' aria-hidden />
            Professional Codes
          </Link>
          <Link
            to='/admin/journal'
            className={journalActive ? tabActive : tabInactive}
          >
            <FiBook className='h-4 w-4 shrink-0' aria-hidden />
            Journal
          </Link>
          <Link
            to='/admin/chats'
            className={chatsActive ? tabActive : tabInactive}
          >
            <FiMessageCircle className='h-4 w-4 shrink-0' aria-hidden />
            Chats
          </Link>
          <Link
            to='/admin/tools'
            className={toolsActive ? tabActive : tabInactive}
          >
            <FiTool className='h-4 w-4 shrink-0' aria-hidden />
            Tools
          </Link>
        </nav>
      </header>
      <main className='p-6'>
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});
